/**
 * Simple hCaptcha Service
 * Handles loading and executing hCaptcha for bot protection
 */

const HCAPTCHA_SITEKEY = '9c92151c-1680-472e-81a8-1bf6674a52bc';
const HCAPTCHA_ONLOAD_CALLBACK = 'doubleHCaptchaOnload';
const HCAPTCHA_SCRIPT_URL = `https://js.hcaptcha.com/1/api.js?onload=${HCAPTCHA_ONLOAD_CALLBACK}&render=explicit`;

let scriptPromise = null;
let initializationPromise = null;
let widgetId = null;
let widgetContainerId = null;
let executeResolve = null;
let executeReject = null;

export async function verifyCheckoutCaptcha(store) {
  if (!store.getters['publicOrgSettings/enhancedBotProtection']) return;
  try {
    store.commit('donation/SET_CAPTCHA_TOKEN', await execute());
  } catch {
    throw new Error(
      'Your donation couldn’t go through because the verification test failed. If you think this was a mistake, please reach out to support@double.giving'
    );
  }
}

/**
 * Load hCaptcha script
 */
async function loadScript() {
  console.log('Loading hc script');
  if (window.hcaptcha) return;
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = HCAPTCHA_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    window[HCAPTCHA_ONLOAD_CALLBACK] = () => {
      console.log('hc script loaded from callback');
      delete window[HCAPTCHA_ONLOAD_CALLBACK];
      resolve();
    };
    script.onerror = () => {
      delete window[HCAPTCHA_ONLOAD_CALLBACK];
      scriptPromise = null;
      reject(new Error('Failed to load hc script'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function isInvalidWidgetIdError(error) {
  return /invalid hcaptcha id|invalid-captcha-id/i.test(error?.message || error || '');
}

function clearWidget() {
  const container = widgetContainerId && document.getElementById(widgetContainerId);
  if (container) {
    container.innerHTML = '';
  }
  widgetId = null;
  widgetContainerId = null;
}

function isWidgetValid() {
  if (widgetId === null || !widgetContainerId || !document.getElementById(widgetContainerId)) {
    return false;
  }

  try {
    window.hcaptcha.getResponse(widgetId);
    return true;
  } catch (error) {
    if (isInvalidWidgetIdError(error)) {
      return false;
    }
    throw error;
  }
}

/**
 * Initialize hCaptcha widget
 * @param {string} containerId - ID of the container element
 */
async function initialize(containerId = 'hcaptcha-container') {
  if (initializationPromise) return initializationPromise;

  initializationPromise = (async () => {
    await loadScript();

    if (!window.hcaptcha) {
      throw new Error('hc library not available');
    }

    if (isWidgetValid()) {
      console.log('hc already initialized, widgetId:', widgetId);
      return widgetId;
    }

    clearWidget();

    // Create container if it doesn't exist
    if (!document.getElementById(containerId)) {
      const container = document.createElement('div');
      container.id = containerId;
      document.body.appendChild(container);
    }

    widgetId = window.hcaptcha.render(containerId, {
      sitekey: HCAPTCHA_SITEKEY,
      size: 'invisible',
      callback: onSuccess,
      'error-callback': onError,
      'expired-callback': onExpired
    });
    widgetContainerId = containerId;

    console.log('hc initialized, widgetId:', widgetId);
    return widgetId;
  })();

  try {
    return await initializationPromise;
  } finally {
    initializationPromise = null;
  }
}

/**
 * Success callback - called when hcaptcha is solved
 */
function onSuccess(token) {
  console.log('hc solved successfully');
  if (executeResolve) {
    executeResolve(token);
    executeResolve = null;
    executeReject = null;
  }
}

/**
 * Error callback - called when hcaptcha fails
 */
function onError() {
  console.error('hc error');
  if (executeReject) {
    executeReject(new Error('hc verification failed'));
    executeResolve = null;
    executeReject = null;
  }
}

/**
 * Expired callback - called when hCaptcha token expires
 */
function onExpired() {
  console.warn('hc token expired');
  if (executeReject) {
    executeReject(new Error('hc token expired'));
    executeResolve = null;
    executeReject = null;
  }
}

/**
 * Execute hc challenge
 * @returns {Promise<string>} hc token
 */
async function execute() {
  await initialize();
  console.log('execute - widgetId:', widgetId);

  try {
    return await executeWidget();
  } catch (error) {
    if (!isInvalidWidgetIdError(error)) {
      throw error;
    }

    clearWidget();
    await initialize();
    return executeWidget();
  }
}

function executeWidget() {
  return new Promise((resolve, reject) => {
    executeResolve = resolve;
    executeReject = reject;

    try {
      // hCaptcha tokens are single-use. Reset before every execution so a retry
      // starts a fresh challenge and invokes the callback with a new token.
      window.hcaptcha.reset(widgetId);
      console.log('Executing hc...');
      window.hcaptcha.execute(widgetId);
    } catch (error) {
      console.error('hc execution failed:', error);
      executeResolve = null;
      executeReject = null;
      reject(error);
    }
  });
}

/**
 * Reset hCaptcha widget
 */
function reset() {
  if (window.hcaptcha && widgetId !== null) {
    try {
      window.hcaptcha.reset(widgetId);
    } catch (error) {
      if (isInvalidWidgetIdError(error)) {
        clearWidget();
        return;
      }
      throw error;
    }
  }
}

export default {
  initialize,
  execute,
  reset
};
